import {
  EMPHASIS_WEIGHT,
  type SemanticTag,
  type TagEmphasis,
} from '@/semantics/SemanticTag';
import { TAG_IMAGE_BUDGET, type TagImage, type TagImageIntent } from '@/semantics/TagImage';
import type { SceneManifest } from './SceneManifest';

/**
 * Which attached photographs go with a render, and what each one is *for*.
 *
 * ## The problem this solves
 *
 * A semantic tag can say "split-rail cedar fence, silvered" and the render still has to invent the
 * rail count, the post spacing, the joint, and the profile of the top rail. For anything whose
 * identity is construction rather than dimension, the only complete answer is a picture of one — so
 * tags carry pictures (`semantics/TagImage.ts`), and this module decides which of them travel with a
 * given frame and how the prompt introduces each.
 *
 * ## Bound to a silhouette, not floated beside the frame
 *
 * The interesting part is not attaching an image, it is *binding* it. A photograph handed to an image
 * editor with no statement of what it refers to is read as a second view of the scene, and its
 * pergola, its planting and its sky get composited into a frame that had none of them. The existing
 * generic reference wording guards against that by forbidding almost everything — which is right for
 * a mood board and useless for a fence, because it also forbids the one thing the picture was
 * attached to achieve.
 *
 * So a reference is resolved against the manifest and carries an **anchor**: the key colour that
 * already marks that object in the ID map. "Image 5 is how the element marked #7fb2ff must look"
 * reuses the exact mechanism the whole capture design rests on, and turns an unbounded instruction
 * into a bounded one — this picture governs those pixels and no others.
 *
 * ## Only what is in frame
 *
 * A tag whose subject the manifest never mentions spends a numbered slot on something the model
 * cannot see, and a legend entry with no referent is worse than a missing one. Site-wide tags are the
 * deliberate exception: their subject is the whole picture.
 *
 * Pure. The panel does the blob conversion and the upload; everything here is data.
 */

export interface RenderReference {
  tagId: string;
  imageId: string;
  intent: TagImageIntent;
  /** The tag's own words — what this picture is a picture of. */
  subject: string;
  /**
   * How the model finds the thing in the frame: the ID-map key colour where the subject has one,
   * otherwise a phrase. Null for a site-wide reference, which governs everything.
   */
  anchor: RenderAnchor | null;
  /** The designer's note about this particular picture — "the joint detail, not the colour". */
  caption: string | null;
  emphasis: TagEmphasis;
}

export interface RenderAnchor {
  /** Key colour in the ID map, when the subject is a plant or a built element. */
  keyColor?: string;
  /** Human description, always present — used alone when there is no key colour. */
  phrase: string;
}

export interface CollectedReferences {
  references: RenderReference[];
  /** The images themselves, in the same order, ready to be appended to the request. */
  images: TagImage[];
  /** Images that could not be sent, and why — surfaced so a dropped picture is never silent. */
  skipped: string[];
}

export interface CollectOptions {
  /** Slots already spent on photographs the designer attached to this particular render. */
  reservedSlots?: number;
  /** Ceiling on references in one request. Defaults to the budget. */
  limit?: number;
}

/**
 * Rank order when there are more pictures than slots.
 *
 * Intent outranks emphasis deliberately. A `match` picture is load-bearing — without it the object
 * is rendered wrong — while a `mood` picture only tints a result that would otherwise still be
 * correct, however loudly its tag was marked. Losing the fence to a palette suggestion because
 * somebody set the palette tag to "essential" is exactly the trade this ordering refuses.
 */
const INTENT_RANK: Record<TagImageIntent, number> = {
  match: 0,
  material: 1,
  avoid: 2,
  mood: 3,
};

export function collectRenderReferences(
  manifest: SceneManifest,
  tags: readonly SemanticTag[],
  options: CollectOptions = {},
): CollectedReferences {
  const limit = Math.max(
    0,
    (options.limit ?? TAG_IMAGE_BUDGET.perRender) - Math.max(0, options.reservedSlots ?? 0),
  );

  const anchors = buildAnchorIndex(manifest);
  const candidates: { reference: RenderReference; image: TagImage; order: number }[] = [];
  const skipped: string[] = [];

  for (const tag of tags) {
    const images = tag.images ?? [];
    if (images.length === 0) continue;

    // A site tag governs the whole frame, so it never needs to be found in it.
    const anchor = tag.target.kind === 'site' ? null : (anchors.get(tag.id) ?? null);
    if (anchor === null && tag.target.kind !== 'site') {
      skipped.push(
        `${images.length} picture${images.length === 1 ? '' : 's'} on “${tag.label}” — its subject is not in this frame.`,
      );
      continue;
    }

    for (const image of images) {
      candidates.push({
        image,
        order: INTENT_RANK[image.intent] * 10 + (3 - EMPHASIS_WEIGHT[tag.emphasis]),
        reference: {
          tagId: tag.id,
          imageId: image.id,
          intent: image.intent,
          subject: tag.label,
          anchor,
          caption: image.caption ?? null,
          emphasis: tag.emphasis,
        },
      });
    }
  }

  // Stable within a rank: `createdAt` already ordered the tag list, and re-sorting on a value that
  // does not change would only make the reference numbering jitter between two renders of one frame.
  const ordered = candidates
    .map((candidate, index) => ({ candidate, index }))
    .sort((a, b) => a.candidate.order - b.candidate.order || a.index - b.index)
    .map((entry) => entry.candidate);

  const kept = ordered.slice(0, limit);
  for (const dropped of ordered.slice(limit)) {
    skipped.push(
      `“${dropped.reference.subject}” (${dropped.reference.intent}) — no room left in this request.`,
    );
  }

  return {
    references: kept.map((entry) => entry.reference),
    images: kept.map((entry) => entry.image),
    skipped,
  };
}

/**
 * Every tag the manifest actually mentions, with the handle the prompt can point at.
 *
 * Walks the manifest rather than the site: "is this in frame?" is a question the manifest has
 * already answered for every scope, and answering it a second way here would let the reference
 * legend disagree with the descriptions in the same prompt.
 */
function buildAnchorIndex(manifest: SceneManifest): Map<string, RenderAnchor> {
  const anchors = new Map<string, RenderAnchor>();

  for (const structure of manifest.structures) {
    for (const descriptor of structure.semantics) {
      if (!descriptor.tagId) continue;
      anchors.set(descriptor.tagId, {
        keyColor: structure.keyColor,
        phrase: `${structure.name}, the built element marked ${structure.keyColor} in the semantic map`,
      });
    }
  }

  for (const subject of manifest.subjects) {
    for (const descriptor of subject.semantics) {
      if (!descriptor.tagId) continue;
      anchors.set(descriptor.tagId, {
        keyColor: subject.keyColor,
        phrase: `the ${subject.botanicalName} marked ${subject.keyColor} in the semantic map`,
      });
    }
  }

  for (const ground of manifest.ground) {
    for (const descriptor of ground.semantics) {
      if (!descriptor.tagId) continue;
      anchors.set(descriptor.tagId, { phrase: `the ${ground.name} ground surface` });
    }
  }

  for (const place of manifest.places) {
    if (!place.tagId) continue;
    anchors.set(place.tagId, {
      phrase: `the area described as “${place.label}”, ${place.screen.zone.replace(/-/g, ' ')} of frame`,
    });
  }

  return anchors;
}

/* --------------------------------- Prompt wording --------------------------------- */

/**
 * The legend line for one reference.
 *
 * Worded per intent, because these ask for four genuinely different things and a single sentence
 * that covered all four would have to be so hedged it asked for nothing. Each one states what to
 * take *and* what not to — the second half is not padding, it is the clause that stops a reference
 * photograph's background from arriving in the output.
 */
export function describeReference(reference: RenderReference, imageNumber: number): string {
  const where = reference.anchor
    ? reference.anchor.keyColor
      ? `${reference.anchor.phrase} (key colour ${reference.anchor.keyColor})`
      : reference.anchor.phrase
    : 'this whole scene';
  const caption = reference.caption ? ` The designer notes: ${reference.caption}.` : '';

  switch (reference.intent) {
    case 'match':
      return (
        `- Image ${imageNumber} — a photograph of what “${reference.subject}” should be. It governs ` +
        `${where}. Render that element to match this photograph: the same construction, materials, ` +
        `proportions, joint and fixing detail, colour and finish, adapted to the size and position it ` +
        `already occupies in image 1. This is the one place you are expected to add detail that image 1 ` +
        `does not show. Take NOTHING else from this photograph — not its setting, planting, background, ` +
        `light, weather or camera angle, and do not introduce any other object visible in it.${caption}`
      );
    case 'material':
      return (
        `- Image ${imageNumber} — a material sample for “${reference.subject}”. It governs ${where}. ` +
        `Take the surface only: the material itself, its grain, colour, and how it has weathered. Apply ` +
        `it to the shape image 1 already has. Do not reproduce the object the sample belongs to, its ` +
        `shape, or anything else in the photograph.${caption}`
      );
    case 'avoid':
      return (
        `- Image ${imageNumber} — a COUNTER-EXAMPLE for “${reference.subject}”, governing ${where}. ` +
        `The result must not look like this. Nothing in this photograph is to appear in your output; it ` +
        `is here to be avoided, not used.${caption}`
      );
    case 'mood':
      return (
        `- Image ${imageNumber} — a mood reference for “${reference.subject}”. Read it for palette, ` +
        `light quality and register only. It is NOT part of the scene: copy no object, no composition, ` +
        `no camera angle, and add nothing from it that image 1 does not already contain.${caption}`
      );
  }
}
